import { FC, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { changeNewProductField, hideSecondaryPanel } from 'store/productManagement/reducer';
import { Store } from 'components/common/types/Store.types';
import { ProductFieldName } from 'store/productManagement/reducer.types';
import ListOfFiles from 'components/common/Deal/ListOfFiles';
import { CodeNameModel } from 'components/common/types';
import { RouteFrameCode } from 'components/common/types/Deal.types';
import FrameFileUploadButton from 'components/common/Deal/FrameFileUploadButton/FrameFileUploadButton';
import { SectionButtonTheme } from 'lib/SectionButton/SectionButton.types';
import { cloneDeep } from 'lodash';
import { updateFrameSelection } from 'utils/frameList';
import FetchedOptionsCollapsible from 'components/common/Deal/FetchedOptionsCollapsible/FetchedOptionsCollapsible';
import SelectedOptionsCollapsible from 'components/common/Deal/SelectedOptionsCollapsible/SelectedOptionsCollapsible';
import SectionDivider from 'components/pages/Planner/SectionDivider';
import { useFrameListSearch } from 'components/common/Deal/hooks/useFrameListSearch';
import Search from 'lib/Search';
import { ProductFrameListPanelProps } from './ProductFrameListPanel.types';
import SelectionPermissionDropdown from '../SelectionPermissionDropdown/SelectionPermissionDropdown';
import { permissionOptions } from '../../consts';
import { isValueRequired } from '../../utils/isValueRequired';
import { isPanelConfirmDisabled } from '../../utils/isPanelConfirmDisabled';
import { secondaryPanelLayoutStyles } from '../../styles';
import { useProductFrameListState } from './useProductFrameListState';
import { useProductFrameListChangeDetection } from './useProductFrameListChangeDetection';
import { ProductSecondaryPanel } from '../ProductSecondaryPanel/ProductSecondaryPanel';

const MAX_FRAME_FILE_UPLOAD = 5;

const ProductFrameListPanel: FC<ProductFrameListPanelProps> = ({ onCancel, isReadOnly = false }) => {
  const fieldName = ProductFieldName.FRAME_LIST;
  const dispatch = useDispatch();

  const productFrameList = useSelector((state: Store) => state.productManagement.newProduct?.frameList);

  const {
    selectedPermission,
    setSelectedPermission,
    selectedRouteFrameCodes,
    setSelectedRouteFrameCodes,
    uploadedListOfFiles,
    setUploadedListOfFiles,
    initialRouteFrameCodes,
    initialListFiles,
    defaultSelectedPermission,
  } = useProductFrameListState(productFrameList);

  const { hasPermissionOrFrameListSelectionChanges } = useProductFrameListChangeDetection(
    selectedPermission,
    defaultSelectedPermission,
    productFrameList,
    initialRouteFrameCodes,
    selectedRouteFrameCodes,
    initialListFiles,
    uploadedListOfFiles,
  );

  const {
    fetchedOptions,
    searchText,
    setSearchText,
    openCategories,
    onToggleCategory,
    isOptionIncluded,
    isOptionSelected,
  } = useFrameListSearch(selectedRouteFrameCodes);

  const onOptionIncludeExcludeChange = (value: CodeNameModel): void => {
    const frameCodes = cloneDeep(selectedRouteFrameCodes);
    const updatedFrames = updateFrameSelection(frameCodes, value) as RouteFrameCode[];
    setSelectedRouteFrameCodes(updatedFrames);
  };

  const handleRemoveFile = (name: string): void => {
    setUploadedListOfFiles((prev) => prev.filter((file) => file.name !== name));
  };

  const newFieldValue = useMemo(
    () =>
      isValueRequired(selectedPermission.code)
        ? { routeFrameCodes: selectedRouteFrameCodes, listFiles: uploadedListOfFiles }
        : undefined,
    [selectedPermission.code, selectedRouteFrameCodes, uploadedListOfFiles],
  );

  const handleSubmit = (): void => {
    dispatch(changeNewProductField({ fieldName, selectedPermission, value: newFieldValue }));
    dispatch(hideSecondaryPanel());
  };

  return (
    <ProductSecondaryPanel
      title="Frame List"
      onSubmit={handleSubmit}
      onCancel={onCancel}
      isDisabled={
        isPanelConfirmDisabled({
          field: productFrameList,
          selectedPermission,
          newFieldValue,
        }) || isReadOnly
      }
      isChange={hasPermissionOrFrameListSelectionChanges}
      customStyles={secondaryPanelLayoutStyles}
      pendoId="Frame List"
    >
      <SelectionPermissionDropdown
        selectedPermission={selectedPermission}
        setSelectedPermission={setSelectedPermission}
        permissionOptions={permissionOptions}
      />
      {isValueRequired(selectedPermission.code) && (
        <>
          <div className="my-4">
            <SectionDivider />
          </div>
          <div className="mb-6">
            <div className="mb-2">
              <Search value={searchText} placeholder="Search route frame codes" onChange={setSearchText} />
            </div>

            <div className="mb-2">
              <FrameFileUploadButton
                listFiles={initialListFiles}
                uploadedListOfFiles={uploadedListOfFiles}
                setUploadedListOfFiles={setUploadedListOfFiles}
                theme={SectionButtonTheme.DASHED}
                isFileUploadVisible
                maxFileUploads={MAX_FRAME_FILE_UPLOAD}
              />
            </div>

            {fetchedOptions.length ? (
              <FetchedOptionsCollapsible
                fetchedOptions={fetchedOptions}
                onOptionIncludeExcludeChange={onOptionIncludeExcludeChange}
                isOptionIncluded={isOptionIncluded}
                isOptionSelected={isOptionSelected}
                onToggleCategory={onToggleCategory}
                openCategories={openCategories}
              />
            ) : null}

            {searchText?.length >= 3 && !fetchedOptions.length ? (
              <p className="text-center px-5 py-2.5 sub-header-base">No results found</p>
            ) : null}

            {uploadedListOfFiles.length ? (
              <ListOfFiles
                uploadedListOfFiles={uploadedListOfFiles}
                setUploadedListOfFiles={setUploadedListOfFiles}
                onToggleCategory={onToggleCategory}
                openCategories={openCategories}
                onRemoveFile={handleRemoveFile}
                isIncludeExcludeDisabled={false}
              />
            ) : null}

            <SelectedOptionsCollapsible
              setSelectedRouteFrameCodes={setSelectedRouteFrameCodes}
              onOptionIncludeExcludeChange={onOptionIncludeExcludeChange}
              isOptionIncluded={isOptionIncluded}
              isOptionSelected={isOptionSelected}
              selectedRouteFrameCodes={selectedRouteFrameCodes}
              onToggleCategory={onToggleCategory}
              openCategories={openCategories}
              label="Selected"
            />
          </div>
        </>
      )}
    </ProductSecondaryPanel>
  );
};

export default ProductFrameListPanel;
